import { useEffect, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { getCleanedUrlParams, showError } from '@utils';
import { ebikesServices } from '@services';

import {
  filtersModalConditionEbikesChange,
  filtersModalRulesEbikesChange,
  resetModalEbikesChange,
  selectEbikesState,
} from 'store/ebike/ebikeSlice';
import { QUERY_KEYS } from 'constants/queryKeys';
import { getCatalogEbikesRequestPayload } from 'helpers/catalog/ebikes';
import { handleEbikes, handleEnums } from './utils';
import { CATALOG_EBIKES_PAGE_SIZE } from './constants';

export const useEbikesOperations = (fetchOnMount) => {
  const dispatch = useDispatch();

  const { query, isReady } = useRouter();

  const { filters: modalFilters } = useSelector(selectEbikesState);

  const {
    page = 1,
    pageSize = CATALOG_EBIKES_PAGE_SIZE,
    orderBy = 'skuId',
    order = 'asc',
    token,
    query: searchQuery,
    ...others
  } = query;

  const cleanedParams = getCleanedUrlParams({
    page,
    pageSize: parseInt(pageSize, 10) || CATALOG_EBIKES_PAGE_SIZE,
    orderBy,
    order,
    query: searchQuery,
  });

  const payload = getCatalogEbikesRequestPayload(
    others,
    modalFilters?.modalFiltersRules
  );
  const {
    data: ebikesData,
    isLoading: isLoadingEbikes,
    isError: isErrorEbikes,
    error: errorEbikes,
    refetch: getEbikesData,
  } = useQuery(
    [
      QUERY_KEYS.GET_CATALOG_EBIKES_VEHICLES_KEY,
      {
        ...cleanedParams,
        ...others,
        ...modalFilters?.modalFiltersRules,
      },
    ],
    () => {
      return ebikesServices?.getEbikesFilters({
        params: cleanedParams,
        payload,
      });
    },
    { refetchOnMount: true, enabled: fetchOnMount && isReady }
  );

  const ebikes = useMemo(() => handleEbikes(ebikesData?.data), [ebikesData]);

  const total = ebikesData?.meta?.total;

  const { data: enumsData } = useQuery([QUERY_KEYS.GET_ENUMS_KEY], () =>
    ebikesServices?.getEbikeEnums()
  );

  const enums = useMemo(() => handleEnums(enumsData?.data), [enumsData]);

  const resetModalChange = useCallback(() => {
    dispatch(resetModalEbikesChange());
  }, [dispatch]);

  const handleFilterModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionEbikesChange(conditions));
    },
    [dispatch]
  );
  const handleFiltersChange = useCallback(
    (rules) => {
      dispatch(filtersModalRulesEbikesChange(rules));
    },
    [dispatch]
  );

  useEffect(() => {
    if (isErrorEbikes) {
      showError(errorEbikes);
    }
  }, [isErrorEbikes, errorEbikes]);

  return {
    page,
    total,
    loading: isLoadingEbikes,
    ebikes,
    enums,
    pageSize: parseInt(pageSize, 10) || CATALOG_EBIKES_PAGE_SIZE,
    hasErrors: isErrorEbikes,
    errorMessage: errorEbikes,
    filters: modalFilters,
    catalogEbikesQueryParams: cleanedParams,
    catalogEbikesPayload: payload,
    getEbikes: getEbikesData,
    resetModalChange,
    handleFilterModalChange,
    handleFiltersChange,
  };
};
